import React, { useState, useEffect, useRef } from 'react'
import * as api from '../../../lib/api'
import { supabase, proxyStorageUrl } from '../../../lib/supabase'
import { useToast } from '../../../contexts/ToastContext'
import CustomSelect from '../../../components/CustomSelect/CustomSelect'

export default function ProfileSettings({
  profile,
  user,
  isAdmin,
  isOwner,
  canManageGeneralSettings,
  signOut,
  userPvzs,
  setActivePvz
}) {
  const toast = useToast()
  
  // Управление организацией
  const [editingOrg, setEditingOrg] = useState(false)
  const [editOrgName, setEditOrgName] = useState(profile?.org?.name || '')
  const [orgSaving, setOrgSaving] = useState(false)

  // Управление ПВЗ
  const [isAddingPvz, setIsAddingPvz] = useState(false)
  const [newPvz, setNewPvz] = useState({ name: '', platform: 'wb', address: '' })
  const [pvzSaving, setPvzSaving] = useState(false)

  const [editingPvzId, setEditingPvzId] = useState(null)
  const [editPvzData, setEditPvzData] = useState({ name: '', platform: 'wb', address: '' })

  // Инвайты
  const [invites, setInvites] = useState([])
  const [invitesLoading, setInvitesLoading] = useState(false)

  // Аватарка
  const [avatarUploading, setAvatarUploading] = useState(false)
  const fileInputRef = useRef(null)

  // Telegram
  const [telegramLinkCode, setTelegramLinkCode] = useState(null)
  const [telegramLoading, setTelegramLoading] = useState(false)
  const [telegramCodeCopied, setTelegramCodeCopied] = useState(false)

  // Загрузка инвайтов для ПВЗ организации
  useEffect(() => {
    if ((isOwner || isAdmin) && userPvzs?.length > 0) {
      setInvitesLoading(true)
      const loadAllInvites = async () => {
        try {
          const allInvites = []
          for (const pvz of userPvzs) {
            const data = await api.getInvites(pvz.id)
            allInvites.push(...data)
          }
          setInvites(allInvites)
        } catch (err) {
          console.error('Ошибка загрузки инвайтов:', err)
        } finally {
          setInvitesLoading(false)
        }
      }
      loadAllInvites()
    }
  }, [isOwner, isAdmin, userPvzs])

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    setAvatarUploading(true)
    try {
      const publicUrl = await api.uploadAvatar(file, profile.id)
      if (publicUrl) {
        await api.updateProfileAvatar(profile.id, publicUrl)
        profile.avatar_url = publicUrl
        toast.success('Фото профиля обновлено!')
      }
    } catch (err) {
      console.error('Ошибка загрузки фото:', err)
      toast.error(err.message || 'Ошибка загрузки фото')
    } finally {
      setAvatarUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleUpdateOrganization = async (e) => {
    e.preventDefault()
    if (!editOrgName.trim()) return

    if (!isOwner && !canManageGeneralSettings) {
      toast.info('Заявка на изменение названия организации отправлена владельцу на одобрение.')
      setEditingOrg(false)
      return
    }

    setOrgSaving(true)
    try {
      await api.updateOrganization(profile.org_id, { name: editOrgName.trim() })
      toast.success('Название организации обновлено!')
      window.location.reload()
    } catch (err) {
      console.error('Ошибка:', err)
      toast.error('Ошибка: ' + err.message)
    } finally {
      setOrgSaving(false)
    }
  }

  const handleAddPvz = async (e) => {
    e.preventDefault()
    if (!newPvz.name) return

    if (!isOwner && !canManageGeneralSettings) {
      toast.info('Заявка на добавление нового ПВЗ отправлена владельцу на одобрение.')
      setIsAddingPvz(false)
      setNewPvz({ name: '', platform: 'wb', address: '' })
      return
    }

    setPvzSaving(true)
    try {
      await api.createPvz(profile.org_id, newPvz)
      toast.success('ПВЗ успешно добавлен!')
      window.location.reload()
    } catch (err) {
      console.error('Ошибка добавления ПВЗ:', err)
      toast.error('Ошибка: ' + err.message)
    } finally {
      setPvzSaving(false)
    }
  }

  const handleUpdatePvz = async (e) => {
    e.preventDefault()
    if (!editPvzData.name.trim()) return

    if (!isOwner && !canManageGeneralSettings) {
      toast.info(`Заявка на изменение настроек ПВЗ "${editPvzData.name}" отправлена владельцу.`)
      setEditingPvzId(null)
      return
    }

    setPvzSaving(true)
    try {
      await api.updatePvz(editingPvzId, editPvzData)
      toast.success('ПВЗ успешно обновлен!')
      window.location.reload()
    } catch (err) {
      console.error('Ошибка:', err)
      toast.error('Ошибка: ' + err.message)
    } finally {
      setPvzSaving(false)
    }
  }

  const handleDeletePvz = async (pvzId, pvzName) => {
    if (userPvzs.length <= 1) {
      toast.error('Нельзя удалить единственный ПВЗ в организации.')
      return
    }
    const confirmName = window.prompt(`Для удаления ПВЗ "${pvzName}" введите его название:`)
    if (confirmName !== pvzName) {
      if (confirmName !== null) toast.error('Название введено неверно. Удаление отменено.')
      return
    }

    if (!isOwner && !canManageGeneralSettings) {
      toast.info(`Заявка на удаление ПВЗ "${pvzName}" отправлена владельцу на одобрение.`)
      return
    }

    try {
      await api.deletePvz(pvzId)
      toast.success('ПВЗ удален!')
      window.location.reload()
    } catch (err) {
      console.error('Ошибка:', err)
      toast.error('Ошибка: ' + err.message)
    }
  }

  const handleCreateInvite = async (pvzId) => {
    try {
      const newInvite = await api.createInvite(pvzId, profile.org_id)
      setInvites(prev => [newInvite, ...prev])
      toast.success('Инвайт создан!')
    } catch (err) {
      console.error(err)
      toast.error('Ошибка создания инвайта')
    }
  }

  const handleDeleteInvite = async (id) => {
    try {
      await api.deleteInvite(id)
      setInvites(prev => prev.filter(i => i.id !== id))
      toast.success('Инвайт удален')
    } catch (err) {
      console.error(err)
      toast.error('Ошибка удаления инвайта')
    }
  }

  const getRoleBadge = (role) => {
    switch (role) {
      case 'owner': return { text: 'Владелец', class: 'role-badge-owner' }
      case 'admin': return { text: 'Админ', class: 'role-badge-admin' }
      case 'helper': return { text: 'Помощник', class: 'role-badge-helper' }
      default: return { text: 'Сотрудник', class: 'role-badge-employee' }
    }
  }

  return (
    <div className="settings-section animate-fade-in">
      <div className="settings-profile-card">
        <div className="settings-avatar">
          <div className="avatar avatar-xl" style={{ overflow: 'hidden' }}>
            {profile?.avatar_url ? (
              <img src={proxyStorageUrl(profile.avatar_url)} alt="Аватар" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              profile?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
            )}
          </div>
          <input 
            type="file" 
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/jpeg,image/png,image/webp"
            onChange={handleAvatarUpload}
          />
          <button 
            className="btn btn-secondary btn-sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={avatarUploading}
          >
            {avatarUploading ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{animation:'spin 1s linear infinite'}}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg> Загрузка...</> : 'Загрузить фото'}
          </button>
        </div>
        <div className="settings-profile-info">
          <div className="input-group">
            <label>Имя</label>
            <input className="input" type="text" value={profile?.full_name || ''} readOnly />
          </div>
          <div className="input-group">
            <label>Роль</label>
            <input
              className="input"
              type="text"
              value={getRoleBadge(profile?.role).text}
              readOnly
              style={{ color: profile?.role === 'owner' ? 'var(--wb-primary)' : 'var(--text-primary)' }}
            />
          </div>
        </div>
      </div>

      {(isOwner || isAdmin) && (
        <div className="settings-profile-card" style={{ marginTop: '24px', flexDirection: 'column', alignItems: 'stretch', gap: '24px' }}>
          {/* Организация */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '16px', borderBottom: '1px solid var(--surface-border)' }}>
            <div>
              <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>Организация</div>
              <h3 className="settings-section-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '20px' }}>
                {profile?.org?.name || 'Моя организация'}
                <button className="btn btn-ghost btn-sm" style={{ padding: '4px', fontSize: '14px' }} onClick={() => setEditingOrg(true)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
              </h3>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>Владелец</div>
              <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{profile?.full_name}</div>
            </div>
          </div>

          {editingOrg && (
            <form className="animate-fade-in" onSubmit={handleUpdateOrganization} style={{ background: 'var(--surface-hover)', padding: '16px', borderRadius: 'var(--border-radius-md)', marginBottom: '16px' }}>
              <div className="input-group">
                <label>Название организации</label>
                <input className="input" required value={editOrgName} onChange={e => setEditOrgName(e.target.value)} placeholder="Моя компания" />
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button type="button" className="btn btn-ghost" onClick={() => { setEditingOrg(false); setEditOrgName(profile?.org?.name || '') }}>Отмена</button>
                <button type="submit" className="btn btn-primary" disabled={orgSaving}>
                  {orgSaving ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </form>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 className="settings-section-title" style={{ margin: 0, fontSize: '16px' }}>Пункты выдачи</h3>
            {!isAddingPvz && (
              <button className="btn btn-primary btn-sm" onClick={() => setIsAddingPvz(true)} style={{ borderRadius: 'var(--border-radius-full)', padding: '6px 16px' }}>
                + Добавить ПВЗ
              </button>
            )}
          </div>

          {isAddingPvz && (
            <form className="animate-fade-in" onSubmit={handleAddPvz} style={{ background: 'var(--surface-hover)', padding: '16px', borderRadius: 'var(--border-radius-md)', marginBottom: '16px' }}>
              <div className="input-group">
                <label>Название ПВЗ</label>
                <input className="input" required value={newPvz.name} onChange={e => setNewPvz({...newPvz, name: e.target.value})} placeholder="Например: ПВЗ Ленина 12" />
              </div>
              <div className="input-group">
                <CustomSelect
                  label="Платформа"
                  value={newPvz.platform}
                  onChange={val => setNewPvz({...newPvz, platform: val})}
                  options={[
                    { value: 'wb', label: 'Wildberries' },
                    { value: 'ozon', label: 'Ozon' },
                  ]}
                />
              </div>
              <div className="input-group">
                <label>Адрес</label>
                <input className="input" value={newPvz.address} onChange={e => setNewPvz({...newPvz, address: e.target.value})} placeholder="г. Москва, ул. Ленина 12" />
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setIsAddingPvz(false)}>Отмена</button>
                <button type="submit" className="btn btn-primary" disabled={pvzSaving}>
                  {pvzSaving ? 'Сохранение...' : 'Создать ПВЗ'}
                </button>
              </div>
            </form>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
            {userPvzs?.map(pvz => (
              <div key={pvz.id} style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'space-between',
                padding: '20px', 
                background: 'var(--bg-secondary)', 
                border: '1px solid var(--surface-border)', 
                borderRadius: 'var(--border-radius-lg)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: pvz.platform === 'ozon' ? '#005BFF' : 'var(--aurora-start)' }} />
                <div>
                  {editingPvzId === pvz.id ? (
                    <form className="animate-fade-in" onSubmit={handleUpdatePvz} style={{ marginBottom: '12px' }}>
                      <div className="input-group" style={{ marginBottom: '8px' }}>
                        <input className="input" required value={editPvzData.name} onChange={e => setEditPvzData({...editPvzData, name: e.target.value})} placeholder="Название ПВЗ" />
                      </div>
                      <div className="input-group" style={{ marginBottom: '8px' }}>
                        <CustomSelect
                          value={editPvzData.platform}
                          onChange={val => setEditPvzData({...editPvzData, platform: val})}
                          options={[
                            { value: 'wb', label: 'Wildberries' },
                            { value: 'ozon', label: 'Ozon' },
                          ]}
                        />
                      </div>
                      <div className="input-group" style={{ marginBottom: '8px' }}>
                        <input className="input" value={editPvzData.address} onChange={e => setEditPvzData({...editPvzData, address: e.target.value})} placeholder="Адрес ПВЗ" />
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditingPvzId(null)}>Отмена</button>
                        <button type="submit" className="btn btn-primary btn-sm" disabled={pvzSaving}>Сохранить</button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '16px', marginBottom: '4px' }}>{pvz.name}</div>
                          <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{pvz.address || 'Адрес не указан'}</div>
                        </div>
                        <button className="btn btn-ghost btn-sm" style={{ padding: '4px' }} onClick={() => { setEditingPvzId(pvz.id); setEditPvzData({ name: pvz.name, platform: pvz.platform, address: pvz.address || '' }); }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                      </div>
                      <div style={{ marginTop: '12px', display: 'inline-block', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, background: pvz.platform === 'ozon' ? 'rgba(0, 91, 255, 0.1)' : 'rgba(20, 184, 166, 0.1)', color: pvz.platform === 'ozon' ? '#005BFF' : 'var(--aurora-start)' }}>
                        {pvz.platform === 'ozon' ? 'Ozon' : 'Wildberries'}
                      </div>
                    </>
                  )}
                  
                  {(isOwner || isAdmin) && !editingPvzId && (
                    <div data-tour="invite-code-card" style={{ marginTop: '16px', borderTop: '1px solid var(--surface-border)', paddingTop: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>Одноразовые инвайты</span>
                        <button className="btn btn-secondary btn-sm" onClick={() => handleCreateInvite(pvz.id)}>+ Создать</button>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {invites.filter(i => i.pvz_id === pvz.id).length === 0 && (
                          <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Нет активных инвайтов</span>
                        )}
                        {invites.filter(i => i.pvz_id === pvz.id).map(invite => (
                          <div key={invite.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--surface)', padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--surface-border)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <code style={{ fontSize: '13px', color: invite.is_used ? 'var(--text-tertiary)' : 'var(--text-primary)', textDecoration: invite.is_used ? 'line-through' : 'none', fontWeight: 600 }}>{invite.code}</code>
                              {invite.is_used && <span style={{ fontSize: '10px', color: 'var(--danger)', background: 'rgba(239, 68, 68, 0.1)', padding: '2px 6px', borderRadius: '4px', fontWeight: 600 }}>Использован</span>}
                            </div>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              {!invite.is_used && (
                                <button 
                                  type="button"
                                  className="btn btn-primary btn-sm" 
                                  style={{ 
                                    padding: '6px 12px', 
                                    fontSize: '13px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    height: '32px',
                                    minHeight: '32px',
                                    borderRadius: '8px',
                                    flexShrink: 0
                                  }}
                                  onClick={() => {
                                    const inviteUrl = `https://www.pvzraschet.ru/?invite=${invite.code}`;
                                    if (navigator.clipboard && navigator.clipboard.writeText) {
                                      navigator.clipboard.writeText(inviteUrl)
                                        .then(() => toast.success('Ссылка скопирована!'))
                                        .catch(() => toast.error('Не удалось скопировать'));
                                    } else {
                                      const textArea = document.createElement("textarea");
                                      textArea.value = inviteUrl;
                                      textArea.style.position = "fixed";
                                      document.body.appendChild(textArea);
                                      textArea.focus();
                                      textArea.select();
                                      try {
                                        document.execCommand('copy');
                                        toast.success('Ссылка скопирована!');
                                      } catch (err) {
                                        toast.error('Не удалось скопировать');
                                      }
                                      document.body.removeChild(textArea);
                                    }
                                  }}
                                  title="Копировать ссылку для регистрации"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                                    <rect x="8" y="2" width="8" height="4" rx="1"/>
                                  </svg>
                                  Копировать
                                </button>
                              )}
                              <button 
                                type="button"
                                className="btn-icon-delete" 
                                style={{ 
                                  color: 'var(--danger)',
                                  borderColor: 'rgba(239, 68, 68, 0.2)',
                                  background: 'rgba(239, 68, 68, 0.05)',
                                  border: '1px solid rgba(239, 68, 68, 0.2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  height: '32px',
                                  width: '32px',
                                  minWidth: '32px',
                                  minHeight: '32px',
                                  flexShrink: 0,
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  padding: 0,
                                  transition: 'all 0.2s ease'
                                }} 
                                onClick={() => handleDeleteInvite(invite.id)}
                                title="Удалить инвайт"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"/>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                  <line x1="10" y1="11" x2="10" y2="17"/>
                                  <line x1="14" y1="11" x2="14" y2="17"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--surface-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {userPvzs.length > 1 ? (
                    <button 
                      className="btn btn-ghost btn-sm" 
                      style={{ color: 'var(--danger)', fontSize: '13px', padding: '4px 8px' }}
                      onClick={() => handleDeletePvz(pvz.id, pvz.name)}
                    >
                      Удалить
                    </button>
                  ) : <div />}
                  <button 
                    className="btn btn-secondary btn-sm" 
                    onClick={() => {
                      setActivePvz(pvz)
                      toast.success(`ПВЗ "${pvz.name}" выбран как активный.`)
                    }}
                  >
                    Выбрать ПВЗ
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Telegram Block */}
      <div className="settings-profile-card" style={{ marginTop: '24px', flexDirection: 'column', alignItems: 'stretch', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg, #229ED9, #1DA1F2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '20px', flexShrink: 0 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          </div>
          <div style={{ flex: 1 }}>
            <h3 className="settings-section-title" style={{ margin: 0, fontSize: '16px' }}>Telegram-уведомления</h3>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
              Получайте мгновенные уведомления об откликах, задачах и выплатах
            </p>
          </div>
        </div>

        {profile?.telegram_chat_id ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#14B8A6', boxShadow: '0 0 6px #14B8A6' }} />
              <span style={{ fontSize: '14px', fontWeight: 500, color: '#14B8A6' }}>Подключено</span>
              {profile.telegram_linked_at && (
                <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginLeft: '4px' }}>
                  с {new Date(profile.telegram_linked_at).toLocaleDateString('ru-RU')}
                </span>
              )}
            </div>
            <button
              className="btn btn-ghost btn-sm"
              style={{ color: 'var(--danger)', fontSize: '13px' }}
              onClick={async () => {
                if (!confirm('Отключить Telegram-уведомления?')) return
                try {
                  await api.unlinkTelegram(profile.id)
                  profile.telegram_chat_id = null
                  profile.telegram_linked_at = null
                  setTelegramLinkCode(null)
                  toast.success('Telegram отключён')
                } catch {
                  toast.error('Ошибка отключения')
                }
              }}
            >
              Отключить Telegram
            </button>
          </div>
        ) : (
          <div>
            <div style={{ background: 'var(--surface-hover)', borderRadius: 'var(--border-radius-md)', padding: '16px', marginBottom: '12px' }}>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
                <strong>1.</strong> Откройте <a href="https://t.me/pvzraschet_bot" target="_blank" rel="noopener" style={{ color: 'var(--aurora-mid)', fontWeight: 600, textDecoration: 'none' }}>@pvzraschet_bot</a> в Telegram<br/>
                <strong>2.</strong> Нажмите <strong>Start</strong><br/>
                <strong>3.</strong> Отправьте код привязки боту
              </p>

              {telegramLinkCode ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    flex: 1, padding: '12px 16px', background: 'var(--bg-primary, #fff)', border: '2px dashed var(--aurora-mid, #8B5CF6)',
                    borderRadius: '10px', textAlign: 'center', fontSize: '24px', fontWeight: 800, letterSpacing: '6px',
                    fontFamily: 'monospace', color: 'var(--aurora-mid, #8B5CF6)'
                  }}>
                    {telegramLinkCode}
                  </div>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      navigator.clipboard.writeText(telegramLinkCode)
                      setTelegramCodeCopied(true)
                      setTimeout(() => setTelegramCodeCopied(false), 2000)
                    }}
                  >
                    {telegramCodeCopied ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> : 'Копировать'}
                  </button>
                </div>
              ) : (
                <button
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                  disabled={telegramLoading}
                  onClick={async () => {
                    setTelegramLoading(true)
                    try {
                      const code = await api.generateTelegramLinkCode(profile.id)
                      setTelegramLinkCode(code)
                    } catch {
                      toast.error('Ошибка генерации кода')
                    } finally {
                      setTelegramLoading(false)
                    }
                  }}
                >
                  {telegramLoading ? 'Генерация...' : 'Получить код привязки'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <button className="btn btn-danger btn-lg settings-logout" onClick={signOut} style={{ marginTop: '24px' }}>
        Выйти из аккаунта
      </button>
    </div>
  )
}
